import { useQuery } from "@tanstack/react-query";
import { GitBranch, Star } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { api } from "../api";
import { shortRepoUrl } from "../components/issues/repo-select";
import { EmptyState, PageHeader } from "../components/page";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { useIsAdmin } from "../lib/permissions";

export function ReposPage() {
  const navigate = useNavigate();
  const admin = useIsAdmin();
  const query = useQuery({ queryKey: ["repos"], queryFn: api.repos });
  const repos = query.data?.repos ?? [];

  return (
    <>
      <PageHeader
        title="Repos"
        description="Where the work lands. Every issue is scoped to one repository; its stations clone that repo and open the pull request against its default branch."
        actions={
          admin ? (
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings?tab=repos">Manage repos</Link>
            </Button>
          ) : undefined
        }
      />
      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      ) : repos.length === 0 ? (
        <EmptyState
          icon={GitBranch}
          title="No repos yet"
          description={
            admin
              ? "Add the repositories the factory may work on from Settings."
              : "Repos show up here once an admin adds them in Settings."
          }
        >
          {admin && (
            <Button variant="outline" asChild>
              <Link to="/settings?tab=repos">Open Settings</Link>
            </Button>
          )}
        </EmptyState>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {repos.map((repo) => (
            <Card
              key={repo.key}
              className="cursor-pointer gap-3 transition-[transform,box-shadow] duration-200 ease-calm hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none"
              onClick={() => navigate(`/repos/${repo.key}`)}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="size-4 text-muted-foreground" />
                  {repo.name}
                  <span className="font-mono text-xs text-muted-foreground">{repo.key}</span>
                </CardTitle>
                <CardDescription className="truncate font-mono text-xs">
                  {shortRepoUrl(repo.url)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {repo.isDefault && (
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 text-amber-600 dark:text-amber-400"
                    >
                      <Star /> Default
                    </Badge>
                  )}
                  {!repo.active && (
                    <Badge variant="outline" className="text-muted-foreground">
                      Inactive
                    </Badge>
                  )}
                  <Badge variant="outline" className="font-mono">
                    {repo.defaultBranch}
                  </Badge>
                </div>
                <Link
                  to={`/issues?repo=${repo.key}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {repo.issueCount} issue{repo.issueCount === 1 ? "" : "s"}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
